import React, { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import PropTypes from 'prop-types';

import LinearProgress from '@/components/ui-bridge/core/LinearProgress';
import ListRefresh from '@/components/ListRefresh/ListRefresh';
import OrderItem from '@/components/OrderItem/OrderItem';
import UserContext from '@/contexts/UserContext';

function OrdersList({
  data,
  orderShowDetailsAction,
  orderIdFromUrl,
  refreshData,
  showListRefresh,
  checkOne,
  fromPage,
  isClientPanel,
  selectedCheckboxes,
  selectedItem,
  hasMoreItems,
  loadMoreItems,
}) {
  const scrollParentRef = useRef(null);
  const previousOrderIdRef = useRef(orderIdFromUrl);

  const handleOrderActivation = (inspection, index, event) => {
    if (inspection.id !== null) {
      orderShowDetailsAction(index, event);
    }
  };

  useEffect(() => {
    if (orderIdFromUrl && Array.isArray(data) && data.length) {
      const orderIndex = data.findIndex(order => order && String(order.id) === String(orderIdFromUrl));
      if (orderIndex !== -1) {
        orderShowDetailsAction(orderIndex);
      }
    }
  }, []);

  useEffect(() => {
    if (orderIdFromUrl && orderIdFromUrl !== previousOrderIdRef.current && Array.isArray(data) && data.length) {
      const orderIndex = data.findIndex(order => order && String(order.id) === String(orderIdFromUrl));
      if (orderIndex !== -1) {
        orderShowDetailsAction(orderIndex);
      }
    }
    previousOrderIdRef.current = orderIdFromUrl;
  }, [data, orderIdFromUrl, orderShowDetailsAction]);

  return (
    <div ref={scrollParentRef} className="h-full overflow-y-auto">
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMoreItems}
        hasMore={hasMoreItems}
        loader={<LinearProgress style={{ top: '10px' }} key="progress" />}
        useWindow={false}
        getScrollParent={() => scrollParentRef.current}
      >
        {showListRefresh && (
          <div className="mb-[12px] px-[1vw]">
            <button
              type="button"
              className="w-full cursor-pointer border-0 bg-transparent p-0 text-left"
              onClick={refreshData}
            >
              <ListRefresh />
            </button>
          </div>
        )}

        {data.filter(Boolean).map((inspection, index) => (
          <div
            className={`fade-in flex w-full items-center justify-start px-[1vw] py-0 text-left hover:bg-transparent ${
              index === 0 ? 'mt-0' : 'mt-3'
            } ${
              inspection.id === null ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            }`}
            key={inspection.id || `order-${index}`}
            role="button"
            aria-disabled={inspection.id === null}
            tabIndex={inspection.id === null ? -1 : 0}
            onClick={event => handleOrderActivation(inspection, index, event)}
            onKeyDown={event => {
              // Ignore key events coming from nested interactive elements (checkboxes, buttons, links)
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOrderActivation(inspection, index, event);
              }
            }}
          >
            <div className="m-0 box-border max-w-full grow-0 basis-full">
              <UserContext.Consumer>
                {user => (
                  <OrderItem
                    data={inspection}
                    inspectionItemSelect={selectedItem === index}
                    checkOne={checkOne}
                    selectedCheckboxes={selectedCheckboxes}
                    user={user}
                    fromPage={fromPage}
                    isClientPanel={isClientPanel || false}
                  />
                )}
              </UserContext.Consumer>
            </div>
          </div>
        ))}
      </InfiniteScroll>
    </div>
  );
}

OrdersList.propTypes = {
  orderIdFromUrl: PropTypes.string,
  orderShowDetailsAction: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  checkOne: PropTypes.func.isRequired,
  selectedCheckboxes: PropTypes.object.isRequired,
  selectedItem: PropTypes.number,
  loadMoreItems: PropTypes.func.isRequired,
  hasMoreItems: PropTypes.bool.isRequired,
  showListRefresh: PropTypes.bool,
  fromPage: PropTypes.string.isRequired,
  refreshData: PropTypes.func,
  isClientPanel: PropTypes.bool,
  isDisplayingSearchResults: PropTypes.bool,
};

OrdersList.defaultProps = {
  orderIdFromUrl: null,
  showListRefresh: false,
  selectedItem: null,
  refreshData: null,
  isClientPanel: false,
  isDisplayingSearchResults: false,
};

export default OrdersList;
